import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY = 'clicklogos_recreations';
const GUEST_LIMIT = 1;
const LOGGED_BONUS = 2;
const PAID_BONUS = 5;

interface RecreationState {
  used: number;
  limit: number;
  remaining: number;
  tier: 'guest' | 'logged' | 'paid';
}

interface StoredRecreation {
  used: number;
  lastReset: number;
}

export const useRecreationCredits = () => {
  const [state, setState] = useState<RecreationState>({
    used: 0,
    limit: GUEST_LIMIT,
    remaining: GUEST_LIMIT,
    tier: 'guest',
  });
  const [isLoading, setIsLoading] = useState(true);

  // Load credits on mount
  const loadCredits = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        // Guest: use localStorage
        const stored = localStorage.getItem(STORAGE_KEY);
        let used = 0;

        if (stored) {
          try {
            const parsed: StoredRecreation = JSON.parse(stored);
            const twentyFourHours = 24 * 60 * 60 * 1000;
            
            // Reset if older than 24h
            if (Date.now() - parsed.lastReset > twentyFourHours) {
              localStorage.removeItem(STORAGE_KEY);
              used = 0;
            } else {
              used = parsed.used;
            }
          } catch {
            used = 0;
          }
        }

        setState({
          used,
          limit: GUEST_LIMIT,
          remaining: Math.max(0, GUEST_LIMIT - used),
          tier: 'guest',
        });
      } else {
        // Logged in: check if paid
        const { data: purchases } = await supabase
          .from('purchases')
          .select('*')
          .eq('user_id', user.id)
          .eq('product_type', 'brand_kit')
          .limit(1);

        const hasPurchased = purchases && purchases.length > 0;

        // Check admin status
        const { data: roleData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'admin')
          .maybeSingle();

        const isAdmin = !!roleData;

        // Get profile credits usage
        const { data: profile } = await supabase
          .from('profiles')
          .select('credits')
          .eq('id', user.id)
          .single();

        // Admin has unlimited
        if (isAdmin) {
          setState({
            used: 0,
            limit: 999,
            remaining: 999,
            tier: 'paid',
          });
        } else if (hasPurchased) {
          // Paid user: 3 free + 5 bonus = 8 total
          const limit = GUEST_LIMIT + LOGGED_BONUS + PAID_BONUS;
          const used = profile?.credits !== undefined ? (limit - profile.credits) : 0;
          setState({
            used: Math.max(0, used),
            limit,
            remaining: profile?.credits ?? limit,
            tier: 'paid',
          });
        } else {
          // Logged but not paid: 3 total
          const limit = GUEST_LIMIT + LOGGED_BONUS;
          const used = profile?.credits !== undefined ? (limit - profile.credits) : 0;
          setState({
            used: Math.max(0, used),
            limit,
            remaining: profile?.credits ?? limit,
            tier: 'logged',
          });
        }
      }
    } catch (error) {
      console.error('Error loading recreation credits:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCredits();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      loadCredits();
    });

    return () => subscription.unsubscribe();
  }, [loadCredits]);

  // Use a credit
  const useCredit = useCallback(async (): Promise<boolean> => {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      // Guest: update localStorage
      const stored = localStorage.getItem(STORAGE_KEY);
      let currentUsed = 0;

      if (stored) {
        try {
          const parsed: StoredRecreation = JSON.parse(stored);
          currentUsed = parsed.used;
        } catch {
          currentUsed = 0;
        }
      }

      if (currentUsed >= GUEST_LIMIT) {
        return false;
      }

      const newUsed = currentUsed + 1;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        used: newUsed,
        lastReset: Date.now(),
      }));

      setState(prev => ({
        ...prev,
        used: newUsed,
        remaining: Math.max(0, GUEST_LIMIT - newUsed),
      }));

      return true;
    } else {
      // Logged in: use database credits via RPC
      const { data: success } = await supabase.rpc('use_credit', {
        p_user_id: user.id,
      });

      if (success) {
        setState(prev => ({
          ...prev,
          used: prev.used + 1,
          remaining: Math.max(0, prev.remaining - 1),
        }));
        return true;
      }

      return false;
    }
  }, []);

  return {
    state,
    isLoading,
    useCredit,
    refetch: loadCredits,
  };
};
